import { FILE_TYPE } from '@/constants/sms';
import i18n from '@/plugins/i18n';
import axios from 'axios';
import { isIOS } from 'mobile-device-detect';

const getDownloadSpeed = (
    lastReceivedBytes,
    currentReceivedBytes,
    startTime,
    helpers
) => {
    // Calculate download speed
    const currentTime = new Date().getTime();
    const elapsedTime = (currentTime - startTime) / 1000; // Time taken in seconds
    const downloadSpeed =
        (currentReceivedBytes - lastReceivedBytes) / elapsedTime;
    // Update variables for next calculation
    lastReceivedBytes = currentReceivedBytes;
    startTime = currentTime;
    return `${helpers.formatBytes(downloadSpeed)}/s`;
};

const getProcessPercentage = (currentReceivedBytes, totalSize) => {
    return Math.round((currentReceivedBytes * 100) / totalSize);
};

const prepareConfirmMessageVNode = (h, i18n) => {
    return h('div', { class: ['text-center py-3s'] }, [
        h('i', {
            class: ['fas fa-exclamation-triangle text-flush-orange'],
            style: [
                {
                    'font-size': '3rem',
                },
            ],
        }),
        h(
            'div',
            {
                class: ['mt-3s text-semi-xl text-arapawa font-weight-600'],
            },
            [i18n.t('message.waiting_for_processing')]
        ),
        h('div', { class: ['mt-2 text-md text-grey'] }, [
            i18n.t('message.wait_download_done'),
        ]),
    ]);
};

export const DownloaderModule = {
    state: () => ({
        processes: [],
    }),
    mutations: {
        pushProcess(state, data) {
            const item = {
                ...data,
                processPercent: 0,
                speed: '0 B/s',
                cancelTokenSource: null,
            };
            state.processes.push(item);
        },
        removeProcess(state, index) {
            state.processes.splice(index, 1);
        },
    },
    actions: {
        async download({ state, commit }, { downloadFunc, item }) {
            if (isIOS && state.processes.length) {
                const messageNode = prepareConfirmMessageVNode(
                    this._vm.$createElement,
                    i18n
                );
                this._vm.$helpers.alertModal([messageNode], {});
                return;
            }
            commit('pushProcess', item);
            const existingItem = state.processes.find(
                (i) => i.name === item?.name
            );
            if (
                existingItem &&
                downloadFunc &&
                typeof downloadFunc === 'function'
            ) {
                try {
                    existingItem.cancelTokenSource = axios.CancelToken.source();
                    let lastReceivedBytes = 0;
                    let startTime = new Date().getTime();
                    const { data } = await downloadFunc({
                        responseType: 'blob',
                        params: {
                            fileName: existingItem.name,
                        },
                        cancelToken: existingItem.cancelTokenSource.token,
                        onDownloadProgress: (progressEvent) => {
                            const currentReceivedBytes = +progressEvent.loaded;
                            const totalSize = +existingItem.size;

                            // Set speed info to existing item
                            existingItem.speed = getDownloadSpeed(
                                lastReceivedBytes,
                                currentReceivedBytes,
                                startTime,
                                this._vm.$helpers
                            );
                            // Set progress percentage info to existing item
                            existingItem.processPercent = getProcessPercentage(
                                currentReceivedBytes,
                                totalSize
                            );
                        },
                    });
                    this._vm.$helpers.downloadBlob(
                        data,
                        existingItem.name,
                        FILE_TYPE.ZIP
                    );
                } catch (e) {
                    if (!axios.isCancel(e)) {
                        this._vm.$helpers.handleThrowError(e);
                    }
                } finally {
                    const index = state.processes.findIndex(
                        (i) => i.name === item.name
                    );
                    commit('removeProcess', index);
                }
            }
        },
        cancelDownload({ state }, item) {
            const existingItem = state.processes.find(
                (i) => i.name === item.name
            );
            existingItem.cancelTokenSource.cancel();
        },
    },
    namespaced: true,
};
